import { type CopyCatalog } from "types/i18n/CopyCatalog";

/** Copy for the public pages: public header and footer, docs chrome, support, invitations, enterprise billing, health and shared test reports. */
export const PUBLIC_PAGES_COPY = {
  mainNav: {
    features: { en: "Features", "zh-CN": "功能", "zh-TW": "功能" },
    howToStart: { en: "How to Start", "zh-CN": "如何开始", "zh-TW": "如何開始" },
    pricing: { en: "Pricing", "zh-CN": "定价", "zh-TW": "定價" },
    help: { en: "Help", "zh-CN": "帮助", "zh-TW": "說明" },
    contact: { en: "Contact", "zh-CN": "联系我们", "zh-TW": "聯絡我們" },
    supportEmail: { en: "Support Email", "zh-CN": "支持邮箱", "zh-TW": "支援電子郵件" },
    docs: { en: "Docs", "zh-CN": "文档", "zh-TW": "文件" },
    logIn: { en: "Log In", "zh-CN": "登录", "zh-TW": "登入" },
    signUp: { en: "Sign Up", "zh-CN": "注册", "zh-TW": "註冊" },
    openNavigationMenu: { en: "Open navigation menu", "zh-CN": "打开导航菜单", "zh-TW": "開啟導覽選單" },
  },
  publicFooter: {
    copyright: {
      en: "© 2025 Muggle AI. All rights reserved.",
      "zh-CN": "© 2025 Muggle AI。保留所有权利。",
      "zh-TW": "© 2025 Muggle AI。保留所有權利。",
    },
    termsAndPrivacy: { en: "Terms & Privacy", "zh-CN": "条款与隐私", "zh-TW": "條款與隱私權" },
  },
  legalPages: {
    englishOnlyNotice: {
      en: "This page is available in English only.",
      "zh-CN": "本页面仅提供英文版本。",
      "zh-TW": "本頁面僅提供英文版本。",
    },
  },
  authCallback: {
    title: { en: "Sign-in failed", "zh-CN": "登录失败", "zh-TW": "登入失敗" },
    description: {
      en: "We could not complete authentication. This often means the hosted auth provider rejected the request.",
      "zh-CN": "我们无法完成身份验证。这通常表示托管的身份验证服务拒绝了该请求。",
      "zh-TW": "我們無法完成身分驗證。這通常表示託管的驗證服務拒絕了此請求。",
    },
    errorCodeLabel: { en: "Error code:", "zh-CN": "错误代码：", "zh-TW": "錯誤代碼：" },
    detailsLabel: { en: "Details:", "zh-CN": "详情：", "zh-TW": "詳細資料：" },
    suggestionsHeading: { en: "Try this:", "zh-CN": "可以尝试：", "zh-TW": "可以試試：" },
    suggestions: {
      retryLater: {
        en: "Retry after a few minutes (rate-limit lockouts are temporary).",
        "zh-CN": "几分钟后重试（因频率限制导致的锁定是暂时的）。",
        "zh-TW": "幾分鐘後再試一次（因頻率限制造成的鎖定是暫時的）。",
      },
      switchNetwork: {
        en: "Use a different network or browser session.",
        "zh-CN": "换用其他网络或浏览器会话。",
        "zh-TW": "改用其他網路或瀏覽器工作階段。",
      },
      contactSupport: {
        en: "Contact support with this error code and details.",
        "zh-CN": "联系支持团队，并提供此错误代码和详情。",
        "zh-TW": "聯絡支援團隊，並提供此錯誤代碼與詳細資料。",
      },
    },
    backToHome: { en: "Back to home", "zh-CN": "返回首页", "zh-TW": "返回首頁" },
    openSupport: { en: "Open support", "zh-CN": "打开支持页面", "zh-TW": "前往支援頁面" },
  },
  docs: {
    layout: {
      loadErrorTitle: { en: "Unable to load documentation", "zh-CN": "无法加载文档", "zh-TW": "無法載入文件" },
      loadErrorDescription: {
        en: "There was a problem loading the documentation manifest. Please try again or contact support@muggle-ai.com if the problem persists.",
        "zh-CN": "加载文档目录时出现问题。请重试；如果问题仍然存在，请联系 support@muggle-ai.com。",
        "zh-TW": "載入文件目錄時發生問題。請再試一次；若問題持續發生，請聯絡 support@muggle-ai.com。",
      },
      openNavigationLabel: { en: "Open docs navigation", "zh-CN": "打开文档导航", "zh-TW": "開啟文件導覽" },
      defaultTitle: { en: "Documentation", "zh-CN": "文档", "zh-TW": "文件" },
      defaultSummary: {
        en: "Explore guides, FAQs, and best practices for Muggle Test.",
        "zh-CN": "浏览 Muggle Test 的指南、常见问题和最佳实践。",
        "zh-TW": "瀏覽 Muggle Test 的指南、常見問題與最佳做法。",
      },
    },
    sidebar: {
      searchLabel: { en: "Quick search", "zh-CN": "快速搜索", "zh-TW": "快速搜尋" },
      searchPlaceholder: { en: "Search docs...", "zh-CN": "搜索文档…", "zh-TW": "搜尋文件…" },
    },
    welcome: {
      title: {
        en: "Welcome to Muggle Test Docs",
        "zh-CN": "欢迎使用 Muggle Test 文档",
        "zh-TW": "歡迎使用 Muggle Test 文件",
      },
      description: {
        en: "Learn how to get started, understand core concepts, and integrate Muggle Test into your workflows.",
        "zh-CN": "了解如何上手、掌握核心概念，并将 Muggle Test 集成到你的工作流中。",
        "zh-TW": "了解如何開始使用、掌握核心概念，並將 Muggle Test 整合到你的工作流程中。",
      },
      browseHint: {
        en: "Use the navigation to browse topics. If you are new to Muggle Test, we recommend starting with the getting started guides, then moving on to key concepts and API usage.",
        "zh-CN": "使用导航浏览各个主题。如果你刚开始使用 Muggle Test，建议先阅读入门指南，再了解核心概念和 API 用法。",
        "zh-TW":
          "使用導覽瀏覽各個主題。如果你是 Muggle Test 新手，建議先閱讀入門指南，再進一步了解核心概念與 API 用法。",
      },
      firstReadSuggestion: {
        en: "A good first read is <articleTitle>{{articleTitle}}</articleTitle> in the <sectionLabel>{{sectionLabel}}</sectionLabel> section.",
        "zh-CN":
          "建议先阅读 <sectionLabel>{{sectionLabel}}</sectionLabel> 部分中的 <articleTitle>{{articleTitle}}</articleTitle>。",
        "zh-TW":
          "建議先閱讀 <sectionLabel>{{sectionLabel}}</sectionLabel> 單元中的 <articleTitle>{{articleTitle}}</articleTitle>。",
      },
    },
    article: {
      loadErrorTitle: { en: "Unable to load article", "zh-CN": "无法加载文章", "zh-TW": "無法載入文章" },
      loadErrorDescription: {
        en: "There was a problem loading this documentation article. Please try again or contact support@muggle-ai.com if the issue continues.",
        "zh-CN": "加载这篇文档文章时出现问题。请重试；如果问题持续存在，请联系 support@muggle-ai.com。",
        "zh-TW": "載入這篇文件文章時發生問題。請再試一次；若問題持續發生，請聯絡 support@muggle-ai.com。",
      },
      noArticleTitle: { en: "No article selected", "zh-CN": "未选择文章", "zh-TW": "尚未選擇文章" },
      noArticleDescription: {
        en: "Choose a topic from the sidebar to view its documentation. If you believe an article should be here but is missing, please contact support.",
        "zh-CN": "从侧边栏选择一个主题即可查看对应文档。如果你认为此处应有文章却没有显示，请联系支持团队。",
        "zh-TW": "從側邊欄選擇主題即可查看相關文件。如果你認為這裡應該有文章卻沒有顯示，請聯絡支援團隊。",
      },
      emptyContent: {
        en: "This article does not have any content yet.",
        "zh-CN": "这篇文章暂无内容。",
        "zh-TW": "這篇文章目前沒有內容。",
      },
      englishOnlyNotice: {
        en: "Docs articles are available in English only.",
        "zh-CN": "文档文章目前仅提供英文版本。",
        "zh-TW": "文件文章目前僅提供英文版本。",
      },
    },
    feedback: {
      prompt: { en: "Was this article helpful?", "zh-CN": "这篇文章对你有帮助吗？", "zh-TW": "這篇文章對你有幫助嗎？" },
      markHelpfulLabel: { en: "Mark article as helpful", "zh-CN": "将文章标记为有帮助", "zh-TW": "將文章標記為有幫助" },
      markNotHelpfulLabel: {
        en: "Mark article as not helpful",
        "zh-CN": "将文章标记为没有帮助",
        "zh-TW": "將文章標記為沒有幫助",
      },
      helpful: { en: "Helpful", "zh-CN": "有帮助", "zh-TW": "有幫助" },
      notHelpful: { en: "Not helpful", "zh-CN": "没有帮助", "zh-TW": "沒有幫助" },
      commentHint: {
        en: "Optional: Tell us what we can improve. Your feedback goes directly to the product team.",
        "zh-CN": "可选：告诉我们可以改进的地方。你的反馈会直接发送给产品团队。",
        "zh-TW": "選填：告訴我們可以改進的地方。你的意見回饋會直接傳送給產品團隊。",
      },
      commentPlaceholder: {
        en: "What were you looking for, or what was confusing?",
        "zh-CN": "你在寻找什么？或者哪里让你感到困惑？",
        "zh-TW": "你在找什麼？或是哪裡讓你感到困惑？",
      },
      dismiss: { en: "Dismiss", "zh-CN": "关闭", "zh-TW": "關閉" },
      submit: { en: "Submit feedback", "zh-CN": "提交反馈", "zh-TW": "送出意見回饋" },
    },
    mermaidDiagram: {
      renderErrorTitle: { en: "Diagram rendering error", "zh-CN": "图表渲染出错", "zh-TW": "圖表轉譯錯誤" },
      renderFailedFallback: { en: "Failed to render diagram", "zh-CN": "图表渲染失败", "zh-TW": "無法轉譯圖表" },
      showSource: { en: "Show source", "zh-CN": "显示源代码", "zh-TW": "顯示原始碼" },
      loading: { en: "Loading diagram...", "zh-CN": "正在加载图表…", "zh-TW": "正在載入圖表…" },
    },
  },
  support: {
    title: { en: "Support", "zh-CN": "支持", "zh-TW": "支援" },
    subtitle: {
      en: "Get help from Muggle AI.",
      "zh-CN": "获取 Muggle AI 的帮助。",
      "zh-TW": "向 Muggle AI 尋求協助。",
    },
    contact: {
      title: { en: "Contact Us", "zh-CN": "联系我们", "zh-TW": "聯絡我們" },
      description: {
        en: "The best way to get help is to send us an email. We'll get back to you within 24 hours.",
        "zh-CN": "获取帮助的最佳方式是给我们发送邮件。我们会在 24 小时内回复你。",
        "zh-TW": "取得協助最好的方式是寄電子郵件給我們。我們會在 24 小時內回覆你。",
      },
      emailButton: {
        en: "Email support@muggle-ai.com",
        "zh-CN": "发送邮件至 support@muggle-ai.com",
        "zh-TW": "寄信至 support@muggle-ai.com",
      },
    },
    faq: {
      title: { en: "FAQ", "zh-CN": "常见问题", "zh-TW": "常見問題" },
      description: {
        en: "Frequently asked questions about Muggle Test",
        "zh-CN": "关于 Muggle Test 的常见问题",
        "zh-TW": "關於 Muggle Test 的常見問題",
      },
      loginForms: {
        question: {
          en: "Can it pass login and multi-step forms?",
          "zh-CN": "能处理登录和多步骤表单吗？",
          "zh-TW": "可以通過登入和多步驟表單嗎？",
        },
        answer: {
          en: 'Yes. Add your credentials in the project’s "Login Credentials". We expose only references to the secrets for the runtime to use; values are injected securely into the page via an encrypted channel. The LLM and our operators never see your raw secret values.',
          "zh-CN":
            "可以。在项目的“登录凭据”中添加你的登录凭据。我们只向运行环境提供密钥的引用；密钥值会通过加密通道安全地注入页面。LLM 和我们的运营人员都看不到你的原始密钥值。",
          "zh-TW":
            "可以。在專案的「登入憑證」中新增你的登入憑證。我們只會將密鑰的參照提供給執行環境使用；密鑰值會透過加密通道安全地注入頁面。LLM 與我們的營運人員都看不到你的原始密鑰值。",
        },
      },
      twoFactorCaptcha: {
        question: {
          en: "Can you handle 2FA/CAPTCHA?",
          "zh-CN": "能处理 2FA/CAPTCHA 吗？",
          "zh-TW": "可以處理 2FA/CAPTCHA 嗎？",
        },
        answer: {
          en: "Not yet. Please use a staging/test environment with 2FA and CAPTCHA disabled. Support is on our roadmap.",
          "zh-CN": "暂时不能。请使用已关闭 2FA 和 CAPTCHA 的预发布/测试环境。我们已将此功能列入路线图。",
          "zh-TW": "目前還不行。請使用已停用 2FA 與 CAPTCHA 的預備/測試環境。我們已將此功能列入產品藍圖。",
        },
      },
      platforms: {
        question: {
          en: "Do you test desktop or mobile apps?",
          "zh-CN": "你们测试桌面应用或移动应用吗？",
          "zh-TW": "你們會測試桌面或行動應用程式嗎？",
        },
        answer: {
          en: "We currently test web applications only, targeting desktop viewports.",
          "zh-CN": "我们目前只测试 Web 应用，并以桌面端视口为准。",
          "zh-TW": "我們目前僅測試 Web 應用程式，並以桌面版檢視區為準。",
        },
      },
      unsupported: {
        question: {
          en: "What isn’t supported yet (as of {{date}})",
          "zh-CN": "目前尚不支持的功能（截至 {{date}}）",
          "zh-TW": "目前尚未支援的功能（截至 {{date}}）",
        },
        items: {
          fileUpload: { en: "File upload", "zh-CN": "文件上传", "zh-TW": "檔案上傳" },
          microphoneCamera: {
            en: "Microphone / camera access",
            "zh-CN": "麦克风 / 摄像头访问",
            "zh-TW": "麥克風 / 相機存取",
          },
          dynamicFirstHopUrls: {
            en: "Dynamic first-hop URLs requiring a server-generated session ID (e.g., Auth0 redirects)",
            "zh-CN": "需要服务器生成会话 ID 的动态首跳网址（例如 Auth0 重定向）",
            "zh-TW": "需要伺服器產生工作階段 ID 的動態首跳網址（例如 Auth0 重新導向）",
          },
          dragAndDrop: { en: "Drag-and-drop", "zh-CN": "拖放", "zh-TW": "拖放" },
          rightClick: { en: "Right-click actions", "zh-CN": "右键操作", "zh-TW": "右鍵操作" },
          nestedScrolling: {
            en: "Scrolling inside non-body containers or iframes; horizontal scrolling",
            "zh-CN": "在非 body 容器或 iframe 内滚动；水平滚动",
            "zh-TW": "在非 body 容器或 iframe 內捲動；水平捲動",
          },
        },
      },
      dynamicWebsites: {
        question: {
          en: "Can Muggle Test handle dynamic websites?",
          "zh-CN": "Muggle Test 能处理动态网站吗？",
          "zh-TW": "Muggle Test 能處理動態網站嗎？",
        },
        answer: {
          en: "Yes. It’s built for changing UIs:",
          "zh-CN": "可以。它专为不断变化的界面而设计：",
          "zh-TW": "可以。它專為經常變動的介面而設計：",
        },
        items: {
          selectorTolerance: {
            en: "Selector tolerance across text, styling, and position; tolerates element removals",
            "zh-CN": "选择器可容忍文本、样式和位置的变化，也能容忍元素被移除",
            "zh-TW": "選擇器可容許文字、樣式與位置的變動，也能容許元素被移除",
          },
          loaderWaits: {
            en: "Waits for loaders (≈ 15s default)",
            "zh-CN": "等待加载指示器（默认约 15 秒）",
            "zh-TW": "等待載入指示器（預設約 15 秒）",
          },
          layoutVariance: {
            en: "Handles minor layout and viewport variance",
            "zh-CN": "可应对轻微的布局和视口差异",
            "zh-TW": "可應對輕微的版面與檢視區差異",
          },
          failureScreenshots: {
            en: "On failure, screenshots are captured (automatic retries are planned)",
            "zh-CN": "失败时会自动截图（自动重试功能正在规划中）",
            "zh-TW": "失敗時會擷取螢幕截圖（自動重試功能規劃中）",
          },
        },
      },
      timing: {
        question: {
          en: "How long do things take? Any limits/timeouts?",
          "zh-CN": "各项操作需要多长时间？有哪些限制或超时？",
          "zh-TW": "各項作業需要多久？有哪些限制或逾時？",
        },
        answer: {
          en: "Deep site scan setup typically takes ~5–30 minutes (site size dependent). Each test script can include up to 30 steps, with a default per‑step timeout of 5 minutes.",
          "zh-CN":
            "深度网站扫描的设置通常需要约 5–30 分钟（取决于网站规模）。每个测试脚本最多可包含 30 个步骤，每个步骤的默认超时时间为 5 分钟。",
          "zh-TW":
            "深度網站掃描的設定通常需要約 5–30 分鐘（視網站規模而定）。每個測試腳本最多可包含 30 個步驟，每個步驟的預設逾時時間為 5 分鐘。",
        },
      },
      parallelRuns: {
        question: { en: "Parallel runs and costs", "zh-CN": "并行运行与费用", "zh-TW": "平行執行與費用" },
        answer: {
          en: "We queue large batches as needed; parallelism and runtime do not change credit usage.",
          "zh-CN": "我们会按需将大批量任务排队；并行度和运行时长都不会改变额度消耗。",
          "zh-TW": "我們會視需要將大量批次排入佇列；平行度與執行時間都不會影響額度用量。",
        },
      },
      ciCd: {
        question: {
          en: "Can I plug Muggle Test into CI/CD?",
          "zh-CN": "可以将 Muggle Test 接入 CI/CD 吗？",
          "zh-TW": "可以將 Muggle Test 整合到 CI/CD 嗎？",
        },
        answer: { en: "Absolutely. Common options:", "zh-CN": "当然可以。常见方式：", "zh-TW": "當然可以。常見做法：" },
        items: {
          scheduledTests: {
            en: "Scheduled tests in Muggle Test",
            "zh-CN": "在 Muggle Test 中设置定时任务",
            "zh-TW": "在 Muggle Test 中設定排程",
          },
          curl: { en: "cURL with API key", "zh-CN": "通过 API 密钥使用 cURL", "zh-TW": "搭配 API 金鑰使用 cURL" },
          githubActions: {
            en: "GitHub Actions with API key",
            "zh-CN": "通过 API 密钥使用 GitHub Actions",
            "zh-TW": "搭配 API 金鑰使用 GitHub Actions",
          },
        },
      },
      results: {
        question: {
          en: "How do I consume test results?",
          "zh-CN": "如何获取测试结果？",
          "zh-TW": "如何取得測試結果？",
        },
        answer: {
          en: "Export reports as HTML, PDF, or TXT; or have results delivered via email/SMS.",
          "zh-CN": "可将报告导出为 HTML、PDF 或 TXT 格式，也可以通过邮件/短信接收结果。",
          "zh-TW": "可將報告匯出為 HTML、PDF 或 TXT 格式，也可以透過電子郵件/簡訊接收結果。",
        },
      },
      dataSecurity: {
        question: { en: "Is my data safe and secure?", "zh-CN": "我的数据安全吗？", "zh-TW": "我的資料安全嗎？" },
        answer: {
          en: "Absolutely. Each user's data is stored separately and encrypted in transit and at rest. Access is limited to the account owner. Results are retained until you delete them. Data is hosted in the US only.",
          "zh-CN":
            "当然。每个用户的数据都单独存储，并在传输和静态存储时加密。只有账户所有者可以访问。结果会一直保留，直到你将其删除。数据仅托管在美国。",
          "zh-TW":
            "當然。每位使用者的資料都分開儲存，並在傳輸與靜態儲存時加密。只有帳戶擁有者可以存取。結果會一直保留，直到你將其刪除。資料僅託管於美國。",
        },
      },
      browser: {
        question: {
          en: "What browser do you use to run tests?",
          "zh-CN": "你们使用什么浏览器运行测试？",
          "zh-TW": "你們使用哪種瀏覽器執行測試？",
        },
        answer: { en: "Chromium (headless).", "zh-CN": "Chromium（无头模式）。", "zh-TW": "Chromium（無頭模式）。" },
      },
    },
  },
  invite: {
    title: { en: "Welcome to Muggle Test", "zh-CN": "欢迎使用 Muggle Test", "zh-TW": "歡迎使用 Muggle Test" },
    description: {
      en: "You have been invited to collaborate on a project.",
      "zh-CN": "你已受邀参与一个项目的协作。",
      "zh-TW": "你已受邀共同協作一個專案。",
    },
    emailLabel: { en: "Email", "zh-CN": "邮箱", "zh-TW": "電子郵件" },
    temporaryPasswordLabel: { en: "Temporary password", "zh-CN": "临时密码", "zh-TW": "臨時密碼" },
    temporaryPasswordHint: {
      en: "Use this password to sign in and then change your password in Account settings.",
      "zh-CN": "使用此密码登录，然后在账户设置中修改密码。",
      "zh-TW": "使用此密碼登入後，請到帳戶設定變更密碼。",
    },
    openProject: { en: "Open project", "zh-CN": "打开项目", "zh-TW": "開啟專案" },
    changePassword: { en: "Change password", "zh-CN": "修改密码", "zh-TW": "變更密碼" },
  },
  orgInvite: {
    cardDescription: {
      en: "Muggle Test enterprise invitation",
      "zh-CN": "Muggle Test 企业版邀请",
      "zh-TW": "Muggle Test 企業版邀請",
    },
    missingToken: {
      title: { en: "Invitation link is incomplete", "zh-CN": "邀请链接不完整", "zh-TW": "邀請連結不完整" },
      description: {
        en: "The invitation link you followed is missing its token. Ask the person who invited you to send a fresh invitation email.",
        "zh-CN": "你打开的邀请链接缺少令牌。请让邀请你的人重新发送一封邀请邮件。",
        "zh-TW": "你開啟的邀請連結缺少權杖。請邀請你的人重新寄送一封邀請電子郵件。",
      },
    },
    preparing: {
      title: { en: "Preparing your invitation...", "zh-CN": "正在准备你的邀请…", "zh-TW": "正在準備你的邀請…" },
      redirectingToSignIn: {
        en: "Redirecting to sign in...",
        "zh-CN": "正在跳转到登录页面…",
        "zh-TW": "正在重新導向至登入頁面…",
      },
    },
    accepted: {
      title: { en: "You're in!", "zh-CN": "你已加入！", "zh-TW": "你已加入！" },
      description: {
        en: "Invitation accepted. You can now access this organization's projects and shared wallet.",
        "zh-CN": "已接受邀请。现在你可以访问该组织的项目和共享钱包。",
        "zh-TW": "已接受邀請。現在你可以存取此組織的專案與共用錢包。",
      },
      goToOrganization: { en: "Go to organization", "zh-CN": "前往组织", "zh-TW": "前往組織" },
    },
    acceptFailed: {
      title: { en: "Couldn't accept the invitation", "zh-CN": "无法接受邀请", "zh-TW": "無法接受邀請" },
      fallbackMessage: { en: "Failed to accept invitation", "zh-CN": "接受邀请失败", "zh-TW": "接受邀請失敗" },
      wrongAccountHint: {
        en: "If you signed in with a different email than the one that received this invitation, try signing in with the correct account.",
        "zh-CN": "如果你登录时使用的邮箱与收到此邀请的邮箱不同，请改用正确的账户登录。",
        "zh-TW": "如果你登入時使用的電子郵件與收到此邀請的電子郵件不同，請改用正確的帳戶登入。",
      },
      switchAccount: { en: "Switch account", "zh-CN": "切换账户", "zh-TW": "切換帳戶" },
    },
    ready: {
      title: {
        en: "You've been invited to an organization",
        "zh-CN": "你已受邀加入一个组织",
        "zh-TW": "你已受邀加入一個組織",
      },
      description: {
        en: "Accept the invitation to join the organization, share its token wallet, and gain access to its projects.",
        "zh-CN": "接受邀请即可加入该组织、共享其 Token 钱包，并访问其项目。",
        "zh-TW": "接受邀請即可加入此組織、共用其 Token 錢包，並存取其專案。",
      },
      accepting: { en: "Accepting...", "zh-CN": "正在接受…", "zh-TW": "正在接受…" },
      accept: { en: "Accept invitation", "zh-CN": "接受邀请", "zh-TW": "接受邀請" },
    },
  },
  enterpriseBilling: {
    backToDashboard: { en: "Back to dashboard", "zh-CN": "返回控制台", "zh-TW": "返回儀表板" },
    title: { en: "Enterprise billing", "zh-CN": "企业账单", "zh-TW": "企業帳單" },
    subtitle: {
      en: "Organizations you belong to and their subscription status.",
      "zh-CN": "你所属的组织及其订阅状态。",
      "zh-TW": "你所屬的組織及其訂閱狀態。",
    },
    paymentFailedTitle: {
      en: "Payment failed for {{organizationName}}",
      "zh-CN": "{{organizationName}} 付款失败",
      "zh-TW": "{{organizationName}} 付款失敗",
    },
    paymentFailedDescription: {
      en: "Update your payment method to restore access for your organization members.",
      "zh-CN": "请更新付款方式，以恢复组织成员的访问权限。",
      "zh-TW": "請更新付款方式，以恢復組織成員的存取權限。",
    },
    loadingOrganizations: { en: "Loading organizations...", "zh-CN": "正在加载组织…", "zh-TW": "正在載入組織…" },
    loadError: {
      title: { en: "Couldn't load your organizations", "zh-CN": "无法加载你的组织", "zh-TW": "無法載入你的組織" },
      fallbackMessage: { en: "Failed to load organizations", "zh-CN": "加载组织失败", "zh-TW": "載入組織失敗" },
      tryAgain: { en: "Try again", "zh-CN": "重试", "zh-TW": "再試一次" },
    },
    empty: {
      title: { en: "No enterprise organizations", "zh-CN": "没有企业组织", "zh-TW": "沒有企業組織" },
      description: {
        en: "You don't belong to any enterprise organizations yet. Personal plan billing is managed from your dashboard.",
        "zh-CN": "你尚未加入任何企业组织。个人套餐的账单可在控制台中管理。",
        "zh-TW": "你尚未加入任何企業組織。個人方案的帳單可在儀表板中管理。",
      },
      goToPersonalBilling: { en: "Go to personal billing", "zh-CN": "前往个人账单", "zh-TW": "前往個人帳單" },
    },
    organizationRow: {
      capacityLevel: {
        en: { one: "{{count}} concurrent workflow", other: "{{count}} concurrent workflows" },
        "zh-CN": { one: "{{count}} 个并发工作流", other: "{{count}} 个并发工作流" },
        "zh-TW": { one: "{{count}} 個並行工作流程", other: "{{count}} 個並行工作流程" },
      },
      noCapacity: { en: "No capacity subscription", "zh-CN": "尚未订阅容量", "zh-TW": "尚未訂閱容量" },
      yourRole: { en: "Your role: {{role}}", "zh-CN": "你的角色：{{role}}", "zh-TW": "你的角色：{{role}}" },
      manageBilling: { en: "Manage billing", "zh-CN": "管理账单", "zh-TW": "管理帳單" },
      comingSoon: { en: "Coming soon", "zh-CN": "即将推出", "zh-TW": "即將推出" },
    },
    statusLabels: {
      ACTIVE: { en: "Active", "zh-CN": "正常", "zh-TW": "正常" },
      SUSPENDED: { en: "Suspended", "zh-CN": "已暂停", "zh-TW": "已暫停" },
      CANCELED: { en: "Payment failed", "zh-CN": "付款失败", "zh-TW": "付款失敗" },
    },
    roleLabels: {
      OWNER: { en: "OWNER", "zh-CN": "所有者", "zh-TW": "擁有者" },
      ADMIN: { en: "ADMIN", "zh-CN": "管理员", "zh-TW": "管理員" },
      MEMBER: { en: "MEMBER", "zh-CN": "成员", "zh-TW": "成員" },
    },
  },
  health: {
    title: { en: "Service Status", "zh-CN": "服务状态", "zh-TW": "服務狀態" },
    checkNowLabel: { en: "Check backend status now", "zh-CN": "立即检查后端状态", "zh-TW": "立即檢查後端狀態" },
    checking: { en: "Checking...", "zh-CN": "正在检查…", "zh-TW": "正在檢查…" },
    checkNow: { en: "Check now", "zh-CN": "立即检查", "zh-TW": "立即檢查" },
    home: { en: "Home", "zh-CN": "首页", "zh-TW": "首頁" },
    unknownError: { en: "Unknown error", "zh-CN": "未知错误", "zh-TW": "未知錯誤" },
    banner: {
      pending: {
        headline: { en: "Checking service status...", "zh-CN": "正在检查服务状态…", "zh-TW": "正在檢查服務狀態…" },
        subheadline: {
          en: "Reaching out to the backend for the first time.",
          "zh-CN": "正在首次连接后端。",
          "zh-TW": "正在首次連線至後端。",
        },
      },
      operational: {
        headline: { en: "All systems operational", "zh-CN": "所有系统运行正常", "zh-TW": "所有系統運作正常" },
        subheadline: {
          en: "Backend services are responding normally.",
          "zh-CN": "后端服务响应正常。",
          "zh-TW": "後端服務回應正常。",
        },
      },
      degraded: {
        headline: { en: "Service degraded", "zh-CN": "服务性能下降", "zh-TW": "服務效能降低" },
        subheadline: {
          en: "Backend is reachable but responding slower than usual.",
          "zh-CN": "后端可以访问，但响应比平时慢。",
          "zh-TW": "後端可以連線，但回應比平常慢。",
        },
      },
      down: {
        headline: { en: "Service unavailable", "zh-CN": "服务不可用", "zh-TW": "服務無法使用" },
        subheadline: {
          en: "We could not reach the backend on the last check.",
          "zh-CN": "上次检查时无法连接到后端。",
          "zh-TW": "上次檢查時無法連線至後端。",
        },
      },
      unknown: {
        headline: { en: "Status unknown", "zh-CN": "状态未知", "zh-TW": "狀態不明" },
        subheadline: {
          en: "Could not determine current service status.",
          "zh-CN": "无法确定当前的服务状态。",
          "zh-TW": "無法判斷目前的服務狀態。",
        },
      },
    },
    lastChecked: {
      en: "Last checked: <value>{{lastChecked}}</value>",
      "zh-CN": "上次检查：<value>{{lastChecked}}</value>",
      "zh-TW": "上次檢查：<value>{{lastChecked}}</value>",
    },
    responseTime: {
      en: "Response time: <value>{{responseTime}}</value>",
      "zh-CN": "响应时间：<value>{{responseTime}}</value>",
      "zh-TW": "回應時間：<value>{{responseTime}}</value>",
    },
    responseTimeValue: {
      en: "{{responseTimeMs}} ms",
      "zh-CN": "{{responseTimeMs}} 毫秒",
      "zh-TW": "{{responseTimeMs}} 毫秒",
    },
    autoRefresh: { en: "Auto-refresh every 15s", "zh-CN": "每 15 秒自动刷新", "zh-TW": "每 15 秒自動重新整理" },
    backendStatus: {
      operational: { en: "Operational", "zh-CN": "运行正常", "zh-TW": "運作正常" },
      degraded: { en: "Degraded", "zh-CN": "性能下降", "zh-TW": "效能降低" },
      unreachable: { en: "Unreachable", "zh-CN": "无法连接", "zh-TW": "無法連線" },
    },
    backendCardTitle: { en: "Backend", "zh-CN": "后端", "zh-TW": "後端" },
    webClientCardTitle: { en: "Web client", "zh-CN": "Web 客户端", "zh-TW": "Web 用戶端" },
    details: {
      status: { en: "Status", "zh-CN": "状态", "zh-TW": "狀態" },
      uptime: { en: "Uptime", "zh-CN": "运行时长", "zh-TW": "運作時間" },
      serverTime: { en: "Server time", "zh-CN": "服务器时间", "zh-TW": "伺服器時間" },
      release: { en: "Release", "zh-CN": "版本", "zh-TW": "版本" },
      build: { en: "Build", "zh-CN": "构建", "zh-TW": "建置" },
      commit: { en: "Commit", "zh-CN": "提交", "zh-TW": "提交" },
      builtAt: { en: "Built at", "zh-CN": "构建时间", "zh-TW": "建置時間" },
    },
    uptime: {
      seconds: { en: "{{seconds}}s", "zh-CN": "{{seconds}} 秒", "zh-TW": "{{seconds}} 秒" },
      minutes: {
        en: "{{minutes}}m {{seconds}}s",
        "zh-CN": "{{minutes}} 分 {{seconds}} 秒",
        "zh-TW": "{{minutes}} 分 {{seconds}} 秒",
      },
      hours: {
        en: "{{hours}}h {{minutes}}m",
        "zh-CN": "{{hours}} 小时 {{minutes}} 分",
        "zh-TW": "{{hours}} 小時 {{minutes}} 分",
      },
      days: {
        en: "{{days}}d {{hours}}h",
        "zh-CN": "{{days}} 天 {{hours}} 小时",
        "zh-TW": "{{days}} 天 {{hours}} 小時",
      },
    },
    lastError: {
      title: { en: "Last error", "zh-CN": "最近一次错误", "zh-TW": "最近一次錯誤" },
      description: {
        en: "Diagnostic output from the most recent failed health check.",
        "zh-CN": "最近一次失败的健康检查的诊断输出。",
        "zh-TW": "最近一次失敗的健康檢查所產生的診斷輸出。",
      },
    },
  },
  testReportPage: {
    projectIdRequired: { en: "Project ID is required", "zh-CN": "需要项目 ID", "zh-TW": "需要專案 ID" },
    skipToContent: { en: "Skip to report content", "zh-CN": "跳到报告内容", "zh-TW": "跳至報告內容" },
    fallbackProjectName: { en: "Test Project", "zh-CN": "测试项目", "zh-TW": "測試專案" },
    openActionPanelLabel: { en: "Open action panel", "zh-CN": "打开操作面板", "zh-TW": "開啟動作面板" },
    actionPanelTab: { en: "ACTIONS", "zh-CN": "操作", "zh-TW": "動作" },
  },
  enterpriseSalesInquiry: {
    title: { en: "Contact enterprise sales", "zh-CN": "联系企业销售", "zh-TW": "聯絡企業銷售" },
    description: {
      en: "Tell us about your team and needs. We will save your request, then open your email app with a formatted message to {{salesEmail}}.",
      "zh-CN":
        "介绍一下你的团队和需求。我们会先保存你的请求，然后打开你的邮件应用，生成一封发往 {{salesEmail}} 的格式化邮件。",
      "zh-TW":
        "告訴我們你的團隊與需求。我們會先儲存你的請求，再開啟你的郵件應用程式，產生一封寄往 {{salesEmail}} 的格式化郵件。",
    },
    fields: {
      contactName: {
        label: { en: "Full name", "zh-CN": "姓名", "zh-TW": "姓名" },
        placeholder: { en: "Jane Smith", "zh-CN": "张伟", "zh-TW": "陳小明" },
      },
      workEmail: {
        label: { en: "Work email", "zh-CN": "工作邮箱", "zh-TW": "工作電子郵件" },
        placeholder: { en: "jane@acme.com", "zh-CN": "zhangwei@acme.com", "zh-TW": "xiaoming@acme.com" },
      },
      companyName: {
        label: { en: "Company or organization", "zh-CN": "公司或组织", "zh-TW": "公司或組織" },
        placeholder: { en: "Acme Inc.", "zh-CN": "Acme 公司", "zh-TW": "Acme 公司" },
      },
      estimatedSeats: {
        label: { en: "Estimated seats", "zh-CN": "预计席位数", "zh-TW": "預估席位數" },
        placeholder: { en: "Select range", "zh-CN": "选择范围", "zh-TW": "選擇範圍" },
      },
      concurrentWorkflows: {
        label: { en: "Concurrent workflows", "zh-CN": "并发工作流", "zh-TW": "並行工作流程" },
        placeholder: { en: "Select capacity", "zh-CN": "选择容量", "zh-TW": "選擇容量" },
      },
      testCaseScale: {
        label: { en: "Test case scale", "zh-CN": "测试用例规模", "zh-TW": "測試案例規模" },
        placeholder: { en: "Select scale", "zh-CN": "选择规模", "zh-TW": "選擇規模" },
      },
      timeline: {
        label: { en: "Purchase timeline", "zh-CN": "采购时间", "zh-TW": "採購時程" },
        placeholder: { en: "Select timeline", "zh-CN": "选择时间", "zh-TW": "選擇時程" },
      },
      interests: {
        legend: { en: "What are you interested in?", "zh-CN": "你对哪些方面感兴趣？", "zh-TW": "你對哪些方面感興趣？" },
      },
      comments: {
        label: { en: "Additional comments", "zh-CN": "补充说明", "zh-TW": "補充說明" },
        placeholder: {
          en: "Project context, compliance needs, regions, or anything else we should know.",
          "zh-CN": "项目背景、合规要求、部署地区，或其他需要我们了解的信息。",
          "zh-TW": "專案背景、法規遵循需求、部署地區，或其他需要我們瞭解的資訊。",
        },
      },
    },
    validation: {
      contactNameRequired: {
        en: "Please provide your full name.",
        "zh-CN": "请填写你的姓名。",
        "zh-TW": "請填寫你的姓名。",
      },
      workEmailInvalid: {
        en: "Please provide a valid work email.",
        "zh-CN": "请填写有效的工作邮箱。",
        "zh-TW": "請填寫有效的工作電子郵件。",
      },
      companyNameRequired: {
        en: "Please provide your company or organization.",
        "zh-CN": "请填写你的公司或组织。",
        "zh-TW": "請填寫你的公司或組織。",
      },
      saveFailed: {
        en: "Failed to save inquiry status. Please try again.",
        "zh-CN": "保存咨询状态失败，请重试。",
        "zh-TW": "儲存諮詢狀態失敗，請再試一次。",
      },
    },
    submit: { en: "Notify our sales team", "zh-CN": "通知销售团队", "zh-TW": "通知銷售團隊" },
    submitting: { en: "Notifying our sales team...", "zh-CN": "正在通知销售团队…", "zh-TW": "正在通知銷售團隊…" },
    seatRange: {
      unspecified: { en: "Prefer not to say", "zh-CN": "暂不透露", "zh-TW": "暫不透露" },
      four_to_ten: { en: "4–10 seats", "zh-CN": "4–10 个席位", "zh-TW": "4–10 個席位" },
      eleven_to_twentyfive: { en: "11–25 seats", "zh-CN": "11–25 个席位", "zh-TW": "11–25 個席位" },
      twentysix_to_hundred: { en: "26–100 seats", "zh-CN": "26–100 个席位", "zh-TW": "26–100 個席位" },
      over_hundred: { en: "100+ seats", "zh-CN": "100 个以上席位", "zh-TW": "100 個以上席位" },
    },
    concurrency: {
      unspecified: { en: "Prefer not to say", "zh-CN": "暂不透露", "zh-TW": "暫不透露" },
      twenty_to_fifty: {
        en: "20–50 concurrent workflows",
        "zh-CN": "20–50 个并发工作流",
        "zh-TW": "20–50 個並行工作流程",
      },
      fifty_to_hundred: {
        en: "51–100 concurrent workflows",
        "zh-CN": "51–100 个并发工作流",
        "zh-TW": "51–100 個並行工作流程",
      },
      hundred_plus: {
        en: "100+ concurrent workflows",
        "zh-CN": "100 个以上并发工作流",
        "zh-TW": "100 個以上並行工作流程",
      },
    },
    testCaseScale: {
      unspecified: { en: "Prefer not to say", "zh-CN": "暂不透露", "zh-TW": "暫不透露" },
      under_one_hundred: { en: "Under 100 test cases", "zh-CN": "少于 100 个测试用例", "zh-TW": "少於 100 個測試案例" },
      one_hundred_to_five_hundred: {
        en: "100–500 test cases",
        "zh-CN": "100–500 个测试用例",
        "zh-TW": "100–500 個測試案例",
      },
      five_hundred_to_two_thousand: {
        en: "500–2,000 test cases",
        "zh-CN": "500–2,000 个测试用例",
        "zh-TW": "500–2,000 個測試案例",
      },
      two_thousand_to_ten_thousand: {
        en: "2,000–10,000 test cases",
        "zh-CN": "2,000–10,000 个测试用例",
        "zh-TW": "2,000–10,000 個測試案例",
      },
      ten_thousand_plus: {
        en: "10,000+ test cases",
        "zh-CN": "10,000 个以上测试用例",
        "zh-TW": "10,000 個以上測試案例",
      },
    },
    timeline: {
      unspecified: { en: "Prefer not to say", "zh-CN": "暂不透露", "zh-TW": "暫不透露" },
      asap: { en: "ASAP (under 1 month)", "zh-CN": "尽快（1 个月内）", "zh-TW": "盡快（1 個月內）" },
      one_to_three_months: { en: "1–3 months", "zh-CN": "1–3 个月", "zh-TW": "1–3 個月" },
      exploring: {
        en: "Exploring / no fixed date",
        "zh-CN": "了解中 / 暂无确定时间",
        "zh-TW": "評估中 / 尚無確定時間",
      },
    },
    interest: {
      admin_sub_accounts: {
        en: "Admin, sub-accounts, and org controls",
        "zh-CN": "管理员、子账户和组织管控",
        "zh-TW": "管理員、子帳戶與組織管控",
      },
      shared_projects: { en: "Shared projects across the team", "zh-CN": "团队共享项目", "zh-TW": "團隊共享專案" },
      pay_as_you_go: {
        en: "Pay-as-you-go token usage at scale",
        "zh-CN": "大规模按量付费 Token 用量",
        "zh-TW": "大規模隨用隨付 Token 用量",
      },
      sso_security: {
        en: "SSO, security review, or compliance",
        "zh-CN": "SSO、安全评审或合规",
        "zh-TW": "SSO、安全審查或法規遵循",
      },
      dedicated_support: { en: "Dedicated or priority support", "zh-CN": "专属或优先支持", "zh-TW": "專屬或優先支援" },
    },
  },
} satisfies CopyCatalog;
